import React from 'react';
import { Link } from 'react-router-dom';
import { BodyIcon, ChevronRightIcon, DocIcon, HistoryIcon, PulseIcon } from '../../layout/memberPrimitives';
import { BODY_MAP_ENABLED } from '../../../config/featureFlags';

/**
 * Navigation into the surfaces Home contains but the rail does not.
 *
 * The rail carries Home, Results, Plan, Daily, Messages, Testing and Profile.
 * Reports and History are sub-surfaces of Home — the old Home reached them
 * through a tile grid, and this replaces it.
 *
 * THIS BLOCK MUST NEVER HIDE. Every other block on the dashboard disappears
 * when its data is empty, which is right for content and wrong for navigation:
 * without this, a brand-new member — the one with every content block hidden —
 * would have no route to Intelligence or Reports at all. The links elsewhere on
 * the page ("See all trends", "All stories", an activity row) all sit inside
 * blocks that hide, so none of them can be relied on.
 *
 * Deliberately links only, with no stat line. The dashboard above already shows
 * what is in each of these; repeating "92% complete" here would restate the
 * snapshot chip a few hundred pixels lower.
 */

interface RecordLink {
  href: string;
  label: string;
  icon: React.ReactNode;
}

export default function RecordLinks() {
  const links: RecordLink[] = [
    // Results stays here even though the rail now carries it. This row is the
    // one block on Home that can never hide, so it is where the reachability
    // guarantee lives; a member who reads down the page rather than scanning
    // the rail should still find a door.
    { href: '/results', label: 'Results', icon: <PulseIcon size={18} /> },
    { href: '/reports', label: 'Reports', icon: <DocIcon size={18} /> },
    { href: '/history', label: 'Health history', icon: <HistoryIcon size={18} /> },
  ];

  if (BODY_MAP_ENABLED) {
    links.splice(1, 0, { href: '/body-map', label: 'Body map', icon: <BodyIcon size={18} /> });
  }

  return (
    <nav aria-labelledby="record-links-heading">
      <h2 id="record-links-heading" className="m-eyebrow mb-3">
        Your record
      </h2>
      <ul
        className="grid gap-px list-none p-0 m-0"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
          backgroundColor: 'var(--melio-border)',
          border: '1px solid var(--melio-border)',
        }}
      >
        {links.map((link) => (
          <li key={link.href} style={{ backgroundColor: 'white' }}>
            <Link
              to={link.href}
              className="flex items-center gap-2.5 px-4 py-3.5 melio-lift"
              style={{ color: 'var(--melio-ink)' }}
            >
              <span style={{ color: 'var(--melio-olive-deep)' }} aria-hidden="true">
                {link.icon}
              </span>
              <span className="text-sm flex-1">{link.label}</span>
              <ChevronRightIcon size={14} />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
